var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, runInAction } from "mobx";
import { FastenPortalDto, FastenSearchResult } from "../Infra/fasten.search.dtos";
import { fullNameForUSAState } from "healthex-common/usaStates";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("patients");
export class FastenSearchViewModel {
    constructor(opts) {
        this.currentQuery = null;
        this.nextCursor = null;
        this.totalHits = 0;
        this.isModalOpen = false;
        this.searchQuery = "";
        this.searchResults = null;
        this.isLoadingMore = false;
        this.showFilters = false;
        this.selectedState = "ALL";
        this.sortBy = "default";
        this.zipcode = "";
        this.pendingPortalSelection = null;
        this.failedLogos = {};
        this.fastenSearchManager = opts.container.fastenManager;
        this.parentModel = opts.parentModel;
    }
    get hasMore() {
        var _a, _b;
        return ((_b = (_a = this.searchResults) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) < this.totalHits;
    }
    clearSearch() {
        this.searchResults = null;
        this.currentQuery = null;
        this.nextCursor = null;
        this.totalHits = 0;
        this.pendingPortalSelection = null;
        this.showFilters = false;
        this.failedLogos = {};
    }
    markLogoFailed(id) {
        this.failedLogos[id] = true;
    }
    hasLogoFailed(id) {
        return id ? !!this.failedLogos[id] : false;
    }
    toggleFilters() {
        this.showFilters = !this.showFilters;
    }
    setSelectedState(state) {
        this.selectedState = state;
        void this.triggerSearch();
    }
    setSortBy(sort) {
        this.sortBy = sort;
        if (sort === "location" && this.zipcode) {
            void this.triggerSearch();
        }
        else if (sort === "default") {
            void this.triggerSearch();
        }
    }
    setZipcode(zipcode) {
        this.zipcode = zipcode;
        if (this.sortBy === "location" && zipcode.length === 5) {
            void this.triggerSearch();
        }
    }
    buildFilters() {
        const filters = {};
        filters.locations = [this.selectedState];
        if (this.sortBy === "location" && this.zipcode) {
            filters.sortBy = "location";
            filters.sortByOpts = { locationZipcodes: [this.zipcode] };
        }
        return filters;
    }
    async triggerSearch() {
        var _a;
        if ((_a = this.currentQuery) === null || _a === void 0 ? void 0 : _a.trim()) {
            await this.search(this.currentQuery);
        }
    }
    async search(query) {
        this.currentQuery = query;
        if (!(query === null || query === void 0 ? void 0 : query.trim())) {
            this.searchResults = null;
            this.nextCursor = null;
            this.totalHits = 0;
            return;
        }
        const filters = this.buildFilters();
        const response = await this.fastenSearchManager.search(query, filters);
        if (this.currentQuery === query) {
            runInAction(() => {
                this.searchResults = response.results;
                this.nextCursor = response.nextCursor;
                this.totalHits = response.totalHits;
            });
        }
    }
    async loadMore() {
        if (this.isLoadingMore || !this.hasMore || !this.nextCursor || !this.currentQuery) {
            return;
        }
        const queryAtStart = this.currentQuery;
        this.isLoadingMore = true;
        try {
            const filters = this.buildFilters();
            const response = await this.fastenSearchManager.search(queryAtStart, filters, this.nextCursor);
            if (this.currentQuery === queryAtStart) {
                runInAction(() => {
                    var _a;
                    this.searchResults = [...((_a = this.searchResults) !== null && _a !== void 0 ? _a : []), ...response.results];
                    this.nextCursor = response.nextCursor;
                    this.totalHits = response.totalHits;
                });
            }
        }
        finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
    providerSelected(provider) {
        var _a, _b, _c;
        const brandId = (_a = provider.brandIds) === null || _a === void 0 ? void 0 : _a[0];
        const portalsWithEndpoints = (_c = (_b = provider.portals) === null || _b === void 0 ? void 0 : _b.filter((p) => p.endpoints && p.endpoints.length > 0)) !== null && _c !== void 0 ? _c : [];
        if (!brandId || portalsWithEndpoints.length === 0) {
            console.error("Fasten provider missing required data:", { brandId, provider });
            return;
        }
        if (portalsWithEndpoints.length === 1) {
            void this.parentModel.providerSelectedWithPortal(provider, portalsWithEndpoints[0]);
            return;
        }
        this.pendingPortalSelection = {
            provider,
            portals: portalsWithEndpoints,
        };
    }
    portalSelected(portal) {
        if (!this.pendingPortalSelection)
            return;
        void this.parentModel.providerSelectedWithPortal(this.pendingPortalSelection.provider, portal);
        this.pendingPortalSelection = null;
    }
    cancelPortalSelection() {
        this.pendingPortalSelection = null;
    }
    handleResultClick(result) {
        this.providerSelected(result);
        if (!this.pendingPortalSelection) {
            this.isModalOpen = false;
            this.searchQuery = "";
        }
    }
    handlePortalClick(portal) {
        this.portalSelected(portal);
        this.isModalOpen = false;
        this.searchQuery = "";
    }
    handleOpenModal() {
        this.parentModel.fastenAuthError = null;
        this.parentModel.fastenCustomSearchModalOpen = true;
        this.isModalOpen = true;
    }
    handleCloseModal() {
        this.isModalOpen = false;
        this.parentModel.fastenCustomSearchModalOpen = false;
        this.searchQuery = "";
        this.clearSearch();
    }
    setSearchQuery(query) {
        this.searchQuery = query;
        void this.search(query);
    }
    getPortalDisplayUrl(portalWebsite) {
        if (!portalWebsite)
            return null;
        try {
            return new URL(portalWebsite).hostname;
        }
        catch {
            return portalWebsite;
        }
    }
    hasMultiplePortals(provider) {
        var _a, _b;
        return ((_b = (_a = provider.portals) === null || _a === void 0 ? void 0 : _a.filter((p) => p.endpoints && p.endpoints.length > 0).length) !== null && _b !== void 0 ? _b : 0) > 1;
    }
    formatLocations(locations) {
        if (!locations || locations.length === 0)
            return null;
        if (locations.includes("ALL"))
            return t("Nationwide");
        const stateNames = locations
            .map((code) => {
            const stateRecord = fullNameForUSAState;
            return stateRecord[code];
        })
            .filter((name) => !!name);
        if (stateNames.length === 0)
            return null;
        if (stateNames.length <= 2)
            return stateNames.join(", ");
        const displayedStates = stateNames.slice(0, 2).join(", ");
        const remaining = stateNames.length - 2;
        return `${displayedStates} + ${remaining} ${t("more")}`;
    }
}
__decorate([
    observable,
    __metadata("design:type", Number)
], FastenSearchViewModel.prototype, "totalHits", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], FastenSearchViewModel.prototype, "isModalOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], FastenSearchViewModel.prototype, "searchQuery", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FastenSearchViewModel.prototype, "searchResults", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], FastenSearchViewModel.prototype, "isLoadingMore", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], FastenSearchViewModel.prototype, "showFilters", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], FastenSearchViewModel.prototype, "selectedState", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], FastenSearchViewModel.prototype, "sortBy", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], FastenSearchViewModel.prototype, "zipcode", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FastenSearchViewModel.prototype, "pendingPortalSelection", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], FastenSearchViewModel.prototype, "failedLogos", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "clearSearch", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "markLogoFailed", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "toggleFilters", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "setSelectedState", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "setSortBy", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "setZipcode", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], FastenSearchViewModel.prototype, "search", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], FastenSearchViewModel.prototype, "loadMore", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [FastenSearchResult]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "providerSelected", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [FastenPortalDto]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "portalSelected", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "cancelPortalSelection", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [FastenSearchResult]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "handleResultClick", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [FastenPortalDto]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "handlePortalClick", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "handleOpenModal", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "handleCloseModal", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], FastenSearchViewModel.prototype, "setSearchQuery", null);
